/**
 * The Company View's priority × urgency row: one tile per rating (click to filter the role table),
 * with that rating's urgency split as chips INSIDE the tile (click one to see its roles).
 *
 * ⚠️ ONE ROW, NOT TILES + A MATRIX (Theresa, Sep 29 2026). The count tiles and the priority ×
 * urgency table below them were two boxes stating the same rows twice, and together they pushed the
 * role list off the first screen. A chip is exactly one old matrix cell — same buckets, same
 * red/amber severity, same drill-down — so nothing the matrix said is lost.
 *
 * Red = urgent start but low rating; amber = top rating but no time pressure.
 */
import type { CheckInRole } from '../../../types/taCheckin';
import {
  TIER_COLOR,
  TIER_ORDER,
  URG_LABEL,
  URG_ORDER,
  daysBetween,
  fmtDate,
} from './checkinUi';

export interface MatrixSelection {
  tier: string;
  urg: string;
}

const MATRIX_COLS = [...URG_ORDER, '__none__'];

function matrixBuckets(roles: CheckInRole[]) {
  const buckets: Record<string, Record<string, CheckInRole[]>> = {};
  TIER_ORDER.forEach((t) => {
    buckets[t] = {};
    MATRIX_COLS.forEach((u) => {
      buckets[t][u] = [];
    });
  });
  roles.forEach((r) => {
    const u = (URG_ORDER as readonly string[]).includes(r.urgency) ? r.urgency : '__none__';
    if (buckets[r.priority]) buckets[r.priority][u].push(r);
  });
  return buckets;
}

function cellSeverity(tier: string, urg: string, n: number): 'crit' | 'warn' | '' {
  if (!n) return '';
  if ((tier === 'C' || tier === 'B') && urg === 'urgent') return 'crit';
  if (tier === 'AAA' && (urg === 'in time' || urg === '__none__')) return 'warn';
  return '';
}

const card = (active: boolean) =>
  `rounded-lg border bg-white p-2.5 text-left shadow-sm ${
    active ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'
  }`;

function chipClass(sev: 'crit' | 'warn' | '', selected: boolean): string {
  const tone =
    sev === 'crit'
      ? 'bg-rose-100 text-rose-700 font-semibold'
      : sev === 'warn'
        ? 'bg-amber-100 text-amber-800 font-semibold'
        : 'bg-gray-100 text-gray-700';
  return `rounded px-1.5 py-0.5 text-[11px] leading-4 hover:ring-1 hover:ring-gray-400 ${tone} ${
    selected ? 'ring-2 ring-gray-900' : ''
  }`;
}

export function PriorityUrgencyTiles({
  roles,
  activeTier,
  onSelectTier,
  selection,
  onSelect,
  tableFiltered,
  onToggleTableFilter,
}: {
  roles: CheckInRole[];
  activeTier: string | null;
  onSelectTier: (tier: string | null) => void;
  selection: MatrixSelection | null;
  onSelect: (sel: MatrixSelection | null) => void;
  tableFiltered: boolean;
  onToggleTableFilter: (roles: CheckInRole[]) => void;
}) {
  const buckets = matrixBuckets(roles);
  const count = (t: string) => MATRIX_COLS.reduce((n, u) => n + buckets[t][u].length, 0);
  const hasUrgency = roles.some((r) => r.urgency);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">Priority × urgency</span>
        <span className="text-[11px] text-gray-500">
          click a tile to filter the list, a chip to see its roles ·{' '}
          <span className="rounded bg-rose-100 px-1 text-rose-700">red</span> urgent but low priority ·{' '}
          <span className="rounded bg-amber-100 px-1 text-amber-800">amber</span> top priority, no time pressure
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <button className={`${card(activeTier === null)} hover:border-gray-400`} onClick={() => onSelectTier(null)}>
          <div className="text-xs text-gray-500">Open roles</div>
          <div className="text-xl font-semibold leading-7 text-gray-900">{roles.length}</div>
          <div className="text-[11px] text-gray-400">All clusters</div>
        </button>
        {TIER_ORDER.map((t) => {
          const n = count(t);
          const pct = roles.length ? Math.round((n / roles.length) * 100) : 0;
          return (
            // A div, not a button: the chips are buttons of their own, and a button inside a
            // button is invalid HTML that browsers "fix" by closing the outer one early.
            <div key={t} data-testid={`tier-${t}`} className={card(activeTier === t)}>
              <button
                className="block w-full text-left"
                aria-pressed={activeTier === t}
                onClick={() => onSelectTier(activeTier === t ? null : t)}
              >
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: TIER_COLOR[t] }} />
                  {t === 'Unrated' ? 'Unrated' : `Priority ${t}`}
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xl font-semibold leading-7 text-gray-900">{n}</span>
                  <span className="text-[11px] text-gray-400">{pct}%</span>
                </div>
              </button>
              {hasUrgency && n > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {MATRIX_COLS.filter((u) => buckets[t][u].length).map((u) => {
                    const cell = buckets[t][u].length;
                    const isSel = selection?.tier === t && selection?.urg === u;
                    return (
                      <button
                        key={u}
                        aria-pressed={isSel}
                        className={chipClass(cellSeverity(t, u, cell), isSel)}
                        onClick={() => onSelect(isSel ? null : { tier: t, urg: u })}
                      >
                        {URG_LABEL[u]} {cell}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!hasUrgency && (
        <p className="mt-2 text-[11px] text-gray-500">No urgency data available from the CL check-in tab.</p>
      )}

      {selection && (
        <SelectionDrawer
          selection={selection}
          roles={buckets[selection.tier][selection.urg]}
          onClose={() => onSelect(null)}
          tableFiltered={tableFiltered}
          onToggleTableFilter={onToggleTableFilter}
        />
      )}
    </div>
  );
}

/** The roles behind one chip — what a matrix cell used to open. */
function SelectionDrawer({
  selection,
  roles,
  onClose,
  tableFiltered,
  onToggleTableFilter,
}: {
  selection: MatrixSelection;
  roles: CheckInRole[];
  onClose: () => void;
  tableFiltered: boolean;
  onToggleTableFilter: (roles: CheckInRole[]) => void;
}) {
  const selected = roles
    .slice()
    .sort((a, b) => (a.sheetStart || a.plannedStart || '9999').localeCompare(b.sheetStart || b.plannedStart || '9999'));
  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">
          {URG_LABEL[selection.urg]} × priority {selection.tier === 'Unrated' ? 'unrated' : selection.tier} —{' '}
          {selected.length} role{selected.length === 1 ? '' : 's'}
        </span>
        <button className="text-xs text-gray-500 hover:text-gray-900" onClick={onClose}>
          ✕ close
        </button>
      </div>
      <table className="w-full text-xs">
        <tbody>
          {selected.map((r) => {
            const iso = r.sheetStart || r.plannedStart;
            const d = iso ? daysBetween(iso) : null;
            return (
              <tr key={r.positionId || r.title} className="border-t border-gray-200">
                <td className="py-1 pr-2 font-medium text-gray-900">{r.title}</td>
                <td className="py-1 pr-2 text-gray-500">{r.sheetCluster || r.department}</td>
                <td className="py-1 pr-2">
                  {iso ? (
                    <>
                      {fmtDate(iso)}
                      {d != null && d > 0 && <span className="text-rose-700"> · {d} d overdue</span>}
                    </>
                  ) : (
                    <span className="text-gray-400">no start date</span>
                  )}
                </td>
                <td className="py-1 text-right text-gray-500">{r.recruiter || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        className="mt-2 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
        onClick={() => onToggleTableFilter(selected)}
      >
        {tableFiltered ? 'Clear table filter' : 'Filter table below to these roles'}
      </button>
    </div>
  );
}
